import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Mail, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/newsletter.functions";

type Variant = "footer" | "inline" | "card";

export function NewsletterSignup({
  variant = "footer",
  source,
  eyebrow = "Nyhetsbrev",
  title = "Missa inget från oss",
  description = "Nya husbilar i lager, guider och rese­tips — ~1 mejl i månaden. Aldrig spam.",
}: {
  variant?: Variant;
  source?: string;
  eyebrow?: string;
  title?: string;
  description?: string;
}) {
  const subscribe = useServerFn(subscribeNewsletter);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done">("idle");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    try {
      await subscribe({ data: { email, source: source ?? variant } });
      setState("done");
      setEmail("");
      toast.success("Tack! Du är anmäld.");
    } catch (err) {
      setState("idle");
      toast.error(err instanceof Error ? err.message : "Något gick fel");
    }
  }

  const Form = (
    <form onSubmit={onSubmit} className="mt-5 flex w-full max-w-md flex-col gap-2 sm:flex-row">
      <label className="sr-only" htmlFor={`newsletter-${variant}`}>E-post</label>
      <div className="relative flex-1">
        <Mail size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          id={`newsletter-${variant}`}
          type="email"
          required
          autoComplete="email"
          placeholder="din@epost.se"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={state !== "idle"}
          className="h-12 w-full rounded-full border border-border bg-background pl-10 pr-4 text-sm outline-none transition focus:border-accent"
        />
      </div>
      <button
        type="submit"
        disabled={state !== "idle"}
        className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-70"
      >
        {state === "loading" ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Anmäler…
          </>
        ) : state === "done" ? (
          <>
            <Check size={16} /> Anmäld
          </>
        ) : (
          "Anmäl mig"
        )}
      </button>
    </form>
  );

  if (variant === "card") {
    return (
      <section className="rounded-3xl border border-border bg-card p-8 md:p-10">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="mt-3 font-display text-3xl md:text-4xl leading-tight">
          {title}
        </h2>
        <p className="mt-3 max-w-lg text-sm text-muted-foreground md:text-base">{description}</p>
        {Form}
        <p className="mt-3 text-xs text-muted-foreground">Du kan avanmäla dig när du vill.</p>
      </section>
    );
  }

  if (variant === "inline") {
    return (
      <div className="my-12 rounded-2xl border border-border bg-secondary/40 p-6 md:p-8">
        <p className="eyebrow">{eyebrow}</p>
        <h3 className="mt-2 font-display text-2xl md:text-3xl">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
        {Form}
      </div>
    );
  }

  // footer
  return (
    <div>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      {description && <p className="mt-3 text-sm text-foreground/85">{description}</p>}
      {Form}
    </div>
  );
}
